import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Trash2, Eye, EyeOff, Scissors, ChevronDown, ChevronRight, Pencil, Star, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Login from "./Login";
import { listShows, getShow, deleteShow, setShowVisibility, setShowEra, deleteClip, setClipVisibility, editShow, editClip, restyleClip, getCreatorSettings, updateCreatorSettings } from "../lib/api";

export default function Manage() {
  const { user, loading } = useAuth();
  const [shows, setShows] = useState([]);
  const [open, setOpen] = useState({});
  const [clips, setClips] = useState({});
  const [busy, setBusy] = useState("");
  const [featuredIds, setFeaturedIds] = useState([]);
  const [editShowId, setEditShowId] = useState(null);
  const [showForm, setShowForm] = useState({ title: "", show_date: "" });
  const [editClipId, setEditClipId] = useState(null);
  const [clipForm, setClipForm] = useState({ title: "", caption: "" });
  const [styleForm, setStyleForm] = useState({});

  const load = () => listShows().then((s) => setShows(s.filter((x) => !x.is_deleted)));
  useEffect(() => { if (user) { load(); getCreatorSettings().then((s) => setFeaturedIds(s.featured_clip_ids || [])).catch(() => {}); } }, [user]);

  if (loading) return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!user) return <Login />;

  const toggle = async (id) => {
    const nx = { ...open, [id]: !open[id] };
    setOpen(nx);
    if (nx[id] && !clips[id]) {
      try { const d = await getShow(id); setClips((c) => ({ ...c, [id]: d.clips || [] })); } catch { /* ignore */ }
    }
  };
  const delShow = async (id) => {
    if (!window.confirm("Delete this whole show and hide it from your public page?")) return;
    setBusy(`s-${id}`);
    try { await deleteShow(id); toast.success("Show deleted"); await load(); }
    catch { toast.error("Could not delete"); } finally { setBusy(""); }
  };
  const hideShow = async (s) => {
    setBusy(`sv-${s.id}`);
    try { await setShowVisibility(s.id, !s.is_public); toast.success(s.is_public ? "Hidden from public" : "Now public"); await load(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const flip = async (s) => {
    const era = s.era === "tiktok" ? "archive" : "tiktok";
    setBusy(`e-${s.id}`);
    try { await setShowEra(s.id, era); toast.success(era === "tiktok" ? "Featured as TikTok" : "Moved to Before TikTok"); await load(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const delClip = async (showId, clipId) => {
    setBusy(`c-${clipId}`);
    try { await deleteClip(clipId); setClips((c) => ({ ...c, [showId]: c[showId].filter((x) => x.id !== clipId) })); toast.success("Clip deleted"); }
    catch { toast.error("Could not delete clip"); } finally { setBusy(""); }
  };
  const hideClip = async (showId, clip) => {
    setBusy(`cv-${clip.id}`);
    try {
      await setClipVisibility(clip.id, !clip.is_public);
      setClips((c) => ({ ...c, [showId]: c[showId].map((x) => x.id === clip.id ? { ...x, is_public: !clip.is_public } : x) }));
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const startEditShow = (s) => { setEditShowId(s.id); setShowForm({ title: s.title || "", show_date: (s.created_at || "").slice(0, 10) }); };
  const saveShow = async (s) => {
    setBusy(`se-${s.id}`);
    try { await editShow(s.id, { title: showForm.title, show_date: showForm.show_date || undefined }); toast.success("Show updated"); setEditShowId(null); await load(); }
    catch { toast.error("Could not update"); } finally { setBusy(""); }
  };
  const startEditClip = (clip) => {
    setEditClipId(clip.id);
    setClipForm({ title: clip.title || "", caption: clip.caption || "" });
    const st = clip.style || {};
    setStyleForm({ highlight: st.highlight !== false, ken_burns: st.ken_burns !== false, reactions: st.reactions !== false, position: st.position || "bottom" });
  };
  const saveClip = async (showId, clip) => {
    setBusy(`ce-${clip.id}`);
    try {
      await editClip(clip.id, { title: clipForm.title, caption: clipForm.caption });
      setClips((c) => ({ ...c, [showId]: c[showId].map((x) => x.id === clip.id ? { ...x, ...clipForm } : x) }));
      toast.success("Clip updated"); setEditClipId(null);
    } catch { toast.error("Could not update"); } finally { setBusy(""); }
  };
  const applyStyle = async (showId, clip) => {
    setBusy(`cs-${clip.id}`);
    toast.info("Re-rendering this clip with your new look — hang tight…");
    try {
      await restyleClip(clip.id, styleForm);
      const d = await getShow(showId); setClips((c) => ({ ...c, [showId]: d.clips || [] }));
      toast.success("Clip re-rendered with your style"); setEditClipId(null);
    } catch { toast.error("Could not re-render"); } finally { setBusy(""); }
  };
  const toggleFeatured = async (clipId) => {
    const has = featuredIds.includes(clipId);
    const next = has ? featuredIds.filter((x) => x !== clipId) : [...featuredIds, clipId];
    const prev = featuredIds;
    setFeaturedIds(next);
    try { await updateCreatorSettings({ featured_clip_ids: next }); toast.success(has ? "Removed from Hall of Fame" : "★ Added to Hall of Fame"); }
    catch { toast.error("Failed"); setFeaturedIds(prev); }
  };

  const Section = ({ title, list }) => (
    <div className="mb-10">
      <h2 className="font-display text-2xl tracking-wide">{title} <span className="font-mono text-sm text-muted-foreground">({list.length})</span></h2>
      <div className="mt-3 divide-y divide-border border border-border">
        {list.map((s) => (
          <div key={s.id} data-testid={`manage-show-${s.id}`}>
            <div className="flex items-center gap-3 px-4 py-3">
              <button data-testid={`expand-show-${s.id}`} onClick={() => toggle(s.id)} className="text-muted-foreground hover:text-accent">
                {open[s.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate font-body text-sm font-semibold">{s.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {s.clip_count} clips · {s.era === "tiktok" ? "featured" : "archive"} · {s.is_public ? "public" : "private"}
                </p>
              </div>
              <button data-testid={`edit-show-${s.id}`} onClick={() => (editShowId === s.id ? setEditShowId(null) : startEditShow(s))}
                      className="grid h-8 w-8 place-items-center border border-border text-muted-foreground hover:border-accent hover:text-accent" title="Edit title & date">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`era-${s.id}`} onClick={() => flip(s)} disabled={busy === `e-${s.id}`}
                      className="border border-border px-2.5 py-1 font-mono text-[10px] uppercase hover:border-accent hover:text-accent">
                {s.era === "tiktok" ? "→ archive" : "→ feature"}
              </button>
              <button data-testid={`hide-show-${s.id}`} onClick={() => hideShow(s)} disabled={busy === `sv-${s.id}`}
                      className="grid h-8 w-8 place-items-center border border-border text-muted-foreground hover:border-accent hover:text-accent" title={s.is_public ? "Hide" : "Publish"}>
                {s.is_public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button data-testid={`del-show-${s.id}`} onClick={() => delShow(s.id)} disabled={busy === `s-${s.id}`}
                      className="grid h-8 w-8 place-items-center border border-primary/40 text-primary hover:bg-primary hover:text-white" title="Delete show">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {editShowId === s.id && (
              <div className="border-t border-border bg-card/40 px-4 py-3" data-testid={`edit-show-form-${s.id}`}>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
                  <input data-testid={`edit-show-title-${s.id}`} value={showForm.title} onChange={(e) => setShowForm((f) => ({ ...f, title: e.target.value }))} placeholder="Show title"
                         className="border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-accent" />
                  <input type="date" data-testid={`edit-show-date-${s.id}`} value={showForm.show_date} onChange={(e) => setShowForm((f) => ({ ...f, show_date: e.target.value }))}
                         className="border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />
                  <button data-testid={`save-show-${s.id}`} onClick={() => saveShow(s)} disabled={busy === `se-${s.id}`}
                          className="bg-accent px-4 py-2 font-body text-xs font-semibold text-black">Save</button>
                </div>
              </div>
            )}
            {open[s.id] && (
              <div className="border-t border-border bg-card/40 px-4 py-3" data-testid={`clips-${s.id}`}>
                {!clips[s.id] ? <Loader2 className="h-4 w-4 animate-spin text-accent" /> :
                  clips[s.id].length === 0 ? <p className="font-mono text-xs text-muted-foreground">No clips.</p> :
                  <div className="space-y-1.5">
                    {clips[s.id].map((clip) => (
                      <div key={clip.id} className="border border-border" data-testid={`manage-clip-${clip.id}`}>
                        <div className="flex items-center gap-3 px-3 py-2">
                          <Scissors className="h-3.5 w-3.5 shrink-0 text-accent" />
                          <span className="min-w-0 flex-1 truncate font-body text-xs">{clip.title}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">{clip.is_public === false ? "private" : "public"}</span>
                          <button data-testid={`fame-clip-${clip.id}`} onClick={() => toggleFeatured(clip.id)}
                                  className={`grid h-7 w-7 place-items-center border ${featuredIds.includes(clip.id) ? "border-accent bg-accent/20 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent"}`} title="Hall of Fame">
                            <Star className="h-3.5 w-3.5" fill={featuredIds.includes(clip.id) ? "currentColor" : "none"} />
                          </button>
                          <button data-testid={`edit-clip-${clip.id}`} onClick={() => (editClipId === clip.id ? setEditClipId(null) : startEditClip(clip))}
                                  className="grid h-7 w-7 place-items-center border border-border text-muted-foreground hover:border-accent hover:text-accent" title="Edit title & caption">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`hide-clip-${clip.id}`} onClick={() => hideClip(s.id, clip)} disabled={busy === `cv-${clip.id}`}
                                  className="grid h-7 w-7 place-items-center border border-border text-muted-foreground hover:border-accent hover:text-accent">
                            {clip.is_public === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <button data-testid={`del-clip-${clip.id}`} onClick={() => delClip(s.id, clip.id)} disabled={busy === `c-${clip.id}`}
                                  className="grid h-7 w-7 place-items-center border border-primary/40 text-primary hover:bg-primary hover:text-white">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {editClipId === clip.id && (
                          <div className="space-y-2 border-t border-border bg-background/60 px-3 py-3" data-testid={`edit-clip-form-${clip.id}`}>
                            <input data-testid={`edit-clip-title-${clip.id}`} value={clipForm.title} onChange={(e) => setClipForm((f) => ({ ...f, title: e.target.value }))} placeholder="Clip title"
                                   className="w-full border border-border bg-background px-3 py-2 font-body text-xs text-white outline-none focus:border-accent" />
                            <textarea data-testid={`edit-clip-caption-${clip.id}`} value={clipForm.caption} onChange={(e) => setClipForm((f) => ({ ...f, caption: e.target.value }))} placeholder="Caption" rows={2}
                                      className="w-full border border-border bg-background px-3 py-2 font-body text-xs text-white outline-none focus:border-accent" />
                            <div className="flex gap-2">
                              <button data-testid={`save-clip-${clip.id}`} onClick={() => saveClip(s.id, clip)} disabled={busy === `ce-${clip.id}`}
                                      className="inline-flex items-center gap-1 bg-accent px-3 py-1.5 font-body text-xs font-semibold text-black"><Check className="h-3.5 w-3.5" /> Save text</button>
                              <button onClick={() => setEditClipId(null)} className="inline-flex items-center gap-1 border border-border px-3 py-1.5 font-body text-xs text-muted-foreground hover:text-white"><X className="h-3.5 w-3.5" /> Cancel</button>
                            </div>

                            {/* Clip look — captions & Ken Burns */}
                            <div className="mt-3 border-t border-border pt-3">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Clip look</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {[["highlight", "Karaoke captions"], ["ken_burns", "Ken Burns zoom"], ["reactions", "Emoji reactions"]].map(([k, label]) => (
                                  <button key={k} type="button" data-testid={`style-${k}-${clip.id}`}
                                          onClick={() => setStyleForm((f) => ({ ...f, [k]: !f[k] }))}
                                          className={`border px-3 py-1.5 font-body text-xs ${styleForm[k] ? "border-accent bg-accent/15 text-accent" : "border-border text-muted-foreground hover:border-accent"}`}>
                                    {styleForm[k] ? "✓ " : ""}{label}
                                  </button>
                                ))}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Caption position</span>
                                {["top", "middle", "bottom"].map((p) => (
                                  <button key={p} type="button" data-testid={`style-pos-${p}-${clip.id}`}
                                          onClick={() => setStyleForm((f) => ({ ...f, position: p }))}
                                          className={`border px-2.5 py-1 font-body text-xs capitalize ${styleForm.position === p ? "border-accent bg-accent/15 text-accent" : "border-border text-muted-foreground hover:border-accent"}`}>{p}</button>
                                ))}
                              </div>
                              <button data-testid={`apply-style-${clip.id}`} onClick={() => applyStyle(s.id, clip)} disabled={busy === `cs-${clip.id}`}
                                      className="mt-3 inline-flex items-center gap-1 border border-accent px-3 py-1.5 font-body text-xs font-semibold text-accent hover:bg-accent hover:text-black disabled:opacity-50">
                                {busy === `cs-${clip.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply look &amp; re-render
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const tiktok = shows.filter((s) => s.era === "tiktok");
  const archive = shows.filter((s) => s.era !== "tiktok");

  return (
    <div className="min-h-screen bg-background text-white" data-testid="manage-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-accent"><ArrowLeft className="h-4 w-4" /> Dashboard</a>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Manage content</span>
        </div>
      </header>
      <div className="mx-auto max-w-4xl px-6 py-10">
        <h1 className="font-display text-4xl tracking-wide">Manage your shows &amp; clips</h1>
        <p className="mt-2 font-body text-sm text-muted-foreground">Delete or hide anything, and choose what's featured as a TikTok episode vs the early archive.</p>
        <div className="mt-8">
          {tiktok.length > 0 && <Section title="Late Night with Barth & Friends (featured)" list={tiktok} />}
          <Section title="Barth and Friends — Before TikTok" list={archive} />
        </div>
      </div>
    </div>
  );
}
